import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { ArrowLeft, Building2, FileText, Target, ExternalLink } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CHART_COLORS = ['#002FA7', '#E63946', '#2A9D8F', '#E9C46A', '#F4A261', '#264653'];
const TICK_STYLE = { fontSize: 11, fill: '#999' };
const DOT_SMALL = { r: 3 };

function formatMonth(m) {
  const [y, month] = m.split('-');
  const months = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} ${y.slice(2)}`;
}

export function ConsultantDetailView({ consultant, details, loading, onBack }) {
  // Memoize the filtered list of clients with forms (used twice: count + chart loop)
  const clientsWithForms = useMemo(
    () => (details?.clients || []).filter(c => c.forms_count > 0),
    [details],
  );
  return (
    <div className="space-y-6" data-testid="consultant-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-consultants-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-[#002FA7]/10 rounded-full flex items-center justify-center">
          <span className="text-[#002FA7] font-bold text-xl">
            {consultant.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </span>
        </div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-neutral-900">{consultant.name}</h1>
          <p className="text-neutral-500">{consultant.email}</p>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
        </div>
      ) : details && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard icon={Building2} label="Clients" value={details.clients.length} />
            <StatCard icon={FileText} label="Total formulaires" value={details.total_forms} color="text-[#002FA7]" />
            <StatCard icon={Target} label="Total mots-cles" value={details.clients.reduce((a, c) => a + c.keywords_count, 0)} />
          </div>

          <ClientsTable clients={details.clients} />

          {clientsWithForms.length > 0 && (
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-lg text-neutral-900">
                Evolution des formulaires par client
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {clientsWithForms.map((client, idx) => (
                  <div key={client.id} className="bg-white border border-neutral-200 p-5">
                    <h3 className="font-medium text-neutral-900 mb-4">{client.name}</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={client.evolution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tickFormatter={formatMonth} tick={TICK_STYLE} />
                        <YAxis allowDecimals={false} tick={TICK_STYLE} />
                        <Tooltip labelFormatter={formatMonth} formatter={(val) => [val, 'Formulaires']} />
                        <Line type="monotone" dataKey="forms" stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                          strokeWidth={2} dot={DOT_SMALL} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </div>
            </div>
          )}

          {details.clients.length > 1 && (
            <CombinedChart details={details} />
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = 'text-neutral-900' }) {
  return (
    <div className="bg-white border border-neutral-200 p-5">
      <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <p className={`font-mono text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function ClientsTable({ clients }) {
  return (
    <div className="bg-white border border-neutral-200">
      <div className="p-4 border-b border-neutral-100">
        <h2 className="font-heading font-semibold text-lg text-neutral-900">Clients et formulaires</h2>
      </div>
      {clients.length === 0 ? (
        <div className="p-8 text-center">
          <Building2 className="w-10 h-10 mx-auto mb-2 text-neutral-300" />
          <p className="text-neutral-500">Aucun client assigne</p>
        </div>
      ) : (
        <table className="w-full data-table" data-testid="consultant-clients-table">
          <thead>
            <tr>
              <th>Client</th>
              <th className="text-right">Formulaires</th>
              <th className="text-right">Mots-cles</th>
              <th>Site web</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td><span className="font-medium text-neutral-900">{client.name}</span></td>
                <td className="text-right">
                  <span className={`font-mono text-lg font-bold ${client.forms_count > 0 ? 'text-[#002FA7]' : 'text-neutral-400'}`}>
                    {client.forms_count}
                  </span>
                </td>
                <td className="text-right"><span className="font-mono text-neutral-700">{client.keywords_count}</span></td>
                <td>
                  {client.url ? (
                    <a href={client.url} target="_blank" rel="noreferrer" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" />
                      {client.url.replace(/https?:\/\//, '').slice(0, 30)}
                    </a>
                  ) : <span className="text-neutral-400 text-sm">-</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CombinedChart({ details }) {
  const data = details.months.map(m => {
    const point = { month: m };
    details.clients.forEach(c => {
      const ev = c.evolution.find(e => e.month === m);
      point[c.name] = ev ? ev.forms : 0;
    });
    return point;
  });

  return (
    <div className="bg-white border border-neutral-200 p-5">
      <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">
        Vue combinee — Tous les clients
      </h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="month" tickFormatter={formatMonth} tick={TICK_STYLE} />
          <YAxis allowDecimals={false} tick={TICK_STYLE} />
          <Tooltip labelFormatter={formatMonth} />
          {details.clients.map((client, idx) => (
            <Line key={client.id} type="monotone" dataKey={client.name}
              stroke={CHART_COLORS[idx % CHART_COLORS.length]} strokeWidth={2} dot={DOT_SMALL} />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-4 mt-3 justify-center">
        {details.clients.map((client, idx) => (
          <div key={client.id} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
            <span className="text-neutral-600">{client.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
